import { create } from 'zustand'

export type TabDraft = {
  baseline: string
  current: string
  dirty: boolean
  formFields?: unknown
}

interface TabDraftsState {
  drafts: Map<string, TabDraft>
  /**
   * Tabs whose unsaved work lives somewhere other than a draft string —
   * a composite tab (a duty, an eval case) aggregates ~10 card editors and
   * publishes one boolean here through the editor registry.
   */
  dirtyTabFlags: Set<string>
  closingTabIds: Set<string>
  getDraft: (tabId: string) => TabDraft | undefined
  setDraft: (tabId: string, draft: TabDraft) => void
  /** Drops the tab's draft AND its dirty flag: the work is gone either way. */
  clearDraft: (tabId: string) => void
  setDirtyFlag: (tabId: string, dirty: boolean) => void
  isDirty: (tabId: string) => boolean
  /** True when ANY open tab holds unsaved changes (the unload guard reads it). */
  hasDirty: () => boolean
  isClosing: (tabId: string) => boolean
  markClosing: (tabId: string) => void
  unmarkClosing: (tabId: string) => void
}

// Immutability is load-bearing: all mutations replace the Map/Set so
// Zustand selector subscribers see referential change. Mutating in place
// (e.g. `state.drafts.set(...)`) breaks every consumer that reads via
// selectors. The matching regression test in tabDrafts.test.ts guards this.
//
// Not persisted. Drafts are session-scoped editor state; persisting them
// requires Map/Set JSON adapters and is intentionally out of scope.
export const useTabDrafts = create<TabDraftsState>()((set, get) => ({
  drafts: new Map(),
  dirtyTabFlags: new Set(),
  closingTabIds: new Set(),

  getDraft: (tabId) => get().drafts.get(tabId),

  setDraft: (tabId, draft) =>
    set((s) => ({ drafts: new Map(s.drafts).set(tabId, draft) })),

  // Both sources go together: every caller means "this tab has nothing
  // unsaved left" — the close itself, Discard, a duty refresh, a commit.
  clearDraft: (tabId) =>
    set((s) => {
      if (!s.drafts.has(tabId) && !s.dirtyTabFlags.has(tabId)) return s
      const drafts = new Map(s.drafts)
      drafts.delete(tabId)
      const dirtyTabFlags = new Set(s.dirtyTabFlags)
      dirtyTabFlags.delete(tabId)
      return { drafts, dirtyTabFlags }
    }),

  setDirtyFlag: (tabId, dirty) =>
    set((s) => {
      if (s.dirtyTabFlags.has(tabId) === dirty) return s
      const next = new Set(s.dirtyTabFlags)
      if (dirty) next.add(tabId)
      else next.delete(tabId)
      return { dirtyTabFlags: next }
    }),

  isDirty: (tabId) =>
    Boolean(get().drafts.get(tabId)?.dirty) || get().dirtyTabFlags.has(tabId),

  hasDirty: () => {
    const { drafts, dirtyTabFlags } = get()
    if (dirtyTabFlags.size > 0) return true
    for (const draft of drafts.values()) if (draft.dirty) return true
    return false
  },

  isClosing: (tabId) => get().closingTabIds.has(tabId),

  markClosing: (tabId) =>
    set((s) => {
      if (s.closingTabIds.has(tabId)) return s
      const next = new Set(s.closingTabIds)
      next.add(tabId)
      return { closingTabIds: next }
    }),

  unmarkClosing: (tabId) =>
    set((s) => {
      if (!s.closingTabIds.has(tabId)) return s
      const next = new Set(s.closingTabIds)
      next.delete(tabId)
      return { closingTabIds: next }
    }),
}))
